import React, { useContext, createContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { isTokenExpired } from "../utils/auth";
import API_BASE_URL from "../config/apiConfig";

type Role = "therapist" | "webmaster" | "medic" | "parent" | "webmaster_medic";

interface AuthContextType {
  token: string | null;
  role: Role | null;
  userId: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(localStorage.getItem("token"));
  const [role, setRole] = useState<Role | null>((localStorage.getItem("role") as Role) || null);
  const [userId, setUserId] = useState<string | null>(localStorage.getItem("user_id"));
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("user_id");
    setToken(null);
    setRole(null);
    setUserId(null);
    setError(null);
    navigate("/login");
  };

  useEffect(() => {
    if (token && isTokenExpired(token)) logout();
    const interval = setInterval(() => {
      if (token && isTokenExpired(token)) logout();
    }, 60000);
    return () => clearInterval(interval);
  }, [token]);

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) throw new Error("Adresa de email sau parolă greșite");

      const data = await response.json();

      localStorage.setItem("token", data.token);
      localStorage.setItem("role", data.role);
      localStorage.setItem("user_id", data.user_id);

      if (localStorage.getItem("rememberMe") === "true") {
        localStorage.setItem("email", email);
      }

      setToken(data.token);
      setRole(data.role as Role);
      setUserId(data.user_id);
      setError(null);

      switch (data.role as Role) {
        case "therapist":
          navigate("/therapist/dashboard");
          break;
        case "medic":
          navigate("/medic/dashboard");
          break;
        case "webmaster":
          navigate("/webmaster/dashboard");
          break;
        case "webmaster_medic":
          navigate("/webmaster-medic/appointments");
          break;
        case "parent":
          navigate("/parent/dashboard");
          break;
        default:
          navigate("/login");
      }
    } catch (err: any) {
      setError(err.message || "Eroare de autentificare");
    }
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        role,
        userId,
        login,
        logout,
        isAuthenticated: !!token,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};